import { Grid, DialogContent, DialogActions, DialogProps, Dialog } from '@mui/material';
import { memo, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { styled } from '@mui/styles';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import Table from 'shell/components/shared/table';
import PmiTextField from 'shell/components/shared/text-field';
import { noop } from 'shell/utils/noop';
import { CadastralVM, ResidentsListData, ResidentsListVM } from 'shell/view-models/cadastral';
import { headCellsResidentsList } from '../../form/cadastral-data/constants';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { DateTime } from 'luxon';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { base64ToBlob } from 'shell/utils/base64-to-blob';
import { downloadBlob } from 'front-office/utils/download-blob';
import { transformSipoDate } from 'shell/utils/transform-sipo-date';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';

interface ResidentsListModalComponentProps {
  open: boolean;
  onClose: () => void;
  el?: CadastralVM | null;
}

const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

const ResidentsListModalComponent = ({
  open,
  onClose,
  el,
}: ResidentsListModalComponentProps): React.ReactElement => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [population] = useHttpFetch<HttpResponseList<ResidentsListData>, Array<ResidentsListVM>>(
    `${__PERSON_API_URL__}/persona/dati-catastali/elenco-residenti-terna`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          foglio: el?.sheet || undefined,
          particella: el?.particle || undefined,
          subalterno: el?.subordinate || undefined,
        },
      },
      transformer: (response) => {
        return response.data.payload.list.map((listItem) => ({
          section: listItem.sezione || '',
          catCadastral: listItem.categoriaCatastale || '',
          sheet: listItem.foglio || '',
          particle: listItem.particella || '',
          subordinate: listItem.subalterno || '',
          addressDescription: listItem.descrizioneIndirizzo || '',
          civicNumber: listItem.numeroCivico || '',
          civicExponent: listItem.esponenteCivico || '',
          familyCode: listItem.codiceFamiglia || '',
          surname: listItem.cognome || '',
          name: listItem.nome || '',
          birthDate: listItem.dataNascita
            ? DateTime.fromISO(listItem.dataNascita).toFormat('dd/MM/yyyy')
            : '',
          sex: listItem.sesso,
          parentalRelationship: listItem.rapportoParentela,
          residenceStartDate: listItem.dataInizioResidenza
            ? DateTime.fromISO(listItem.dataInizioResidenza).toFormat('dd/MM/yyyy')
            : '',
          residenceEndDate: listItem.dataFineResidenza
            ? DateTime.fromISO(transformSipoDate(listItem.dataFineResidenza)).toFormat('dd/MM/yyyy')
            : '',
        }));
      },
    }
  );

  const getCsv = useHttpClient<HttpResponse<{ contenutoCsv: string }>>(
    `${__PERSON_API_URL__}/persona/dati-catastali/elenco-residenti-terna/download`,
    {
      options: {
        method: 'POST',
        data: {},
      },
    }
  );

  const list = (population.data as Array<ResidentsListVM>) || [];
  const [data] = list;

  return (
    <PmiDialog
      open={open}
      fullScreen
      fullWidth
      onClose={() => onClose()}
      aria-labelledby="table-dialog-title"
    >
      <PmiDialogTitle id="table-dialog-title" color="white" backgroundColor="primary">
        Elenco residenti terna
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <PmiTextField
              aria-label="Sezione"
              label="Sezione"
              id="section"
              name="section"
              type="text"
              variant="outlined"
              value={el?.section || ''}
              disabled
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <PmiTextField
              aria-label="Foglio"
              label="Foglio"
              id="sheet"
              name="sheet"
              type="text"
              variant="outlined"
              value={el?.sheet || ''}
              disabled
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <PmiTextField
              aria-label="Particella"
              label="Particella"
              id="particle"
              name="particle"
              type="text"
              variant="outlined"
              value={el?.particle || ''}
              disabled
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <PmiTextField
              aria-label="Subalterno"
              label="Subalterno"
              id="subordinate"
              name="subordinate"
              type="text"
              variant="outlined"
              value={el?.subordinate || ''}
              disabled
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <PmiTextField
              aria-label="Cat. catast."
              label="Cat. catast."
              id="catCadastral"
              name="catCadastral"
              type="text"
              variant="outlined"
              value={el?.catCadastral || ''}
              disabled
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PmiTextField
              aria-label="Indirizzo"
              label="Indirizzo"
              id="address"
              name="address"
              type="text"
              variant="outlined"
              value={
                `${data?.addressDescription?.trim() || ''} ${data?.civicNumber?.trim() || ''}${
                  data?.civicExponent && data?.civicExponent.trim() !== '00'
                    ? `${transformExponent(data?.civicExponent.trim())}`
                    : ''
                }` || ''
              }
              disabled
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <CsvExtraction
              onClick={() => {
                (
                  getCsv({
                    data: {
                      foglio: el?.sheet,
                      particella: el?.particle,
                      subalterno: el?.subordinate,
                    },
                  }) as ResponsePromise<HttpResponse<{ contenutoCsv: string }>>
                ).then((res) => {
                  const blob = base64ToBlob(res?.data.payload.contenutoCsv || '');
                  downloadBlob(blob, 'Elenco Residenti Terna.csv');
                });
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Table
              onPageChange={(newPage) => setPage(newPage)}
              onRowsPerPageChange={(newRows) => setRowsPerPage(newRows)}
              count={list?.length || 0}
              onSort={noop}
              head={headCellsResidentsList}
              page={page}
              orders={{}}
              rowsPerPage={rowsPerPage}
              body={list || []}
              hideButtons
              ariaLabel="Elenco residenti terna"
              noSort
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton variant="outlined" color="cdmBrand" onClick={() => onClose()}>
          Chiudi
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ResidentsListModalComponent.displayName = 'Residents List Modal Component';

const ResidentsListModal = memo(ResidentsListModalComponent);

export default ResidentsListModal;
